import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { applyResellerBranding } from "../../utils/branding";

const defaultPermissionPaths = [
	{ permission: "dashboard", path: "/" },
	{ permission: "connections", path: "/connections" },
	{ permission: "tickets", path: "/tickets" },
	{ permission: "contacts", path: "/contacts" },
	{ permission: "quickAnswers", path: "/quickAnswers" },
	{ permission: "reminders", path: "/reminders" },
	{ permission: "agenda", path: "/appointments" },
	{ permission: "orders", path: "/orders" },
	{ permission: "processes", path: "/processes" },
	{ permission: "streaming", path: "/streaming" },
	{ permission: "campaigns", path: "/campaigns" },
	{ permission: "webhooks", path: "/webhooks" },
	{ permission: "internalChat", path: "/internal-chat" },
	{ permission: "funnels", path: "/funnels" },
	{ permission: "chatbot", path: "/chatbot" },
	{ permission: "ai", path: "/ai" },
	{ permission: "sidepanelApps", path: "/apps" },
	{ permission: "reseller", path: "/reseller" },
	{ permission: "users", path: "/users" },
	{ permission: "queues", path: "/queues" },
	{ permission: "labels", path: "/labels" }
];

const adminOnlyPermissions = ["users", "queues", "labels", "webhooks"];

const roleCanUsePermission = (user, permission) => {
	if (!adminOnlyPermissions.includes(permission)) return true;
	return ["admin", "superadmin", "owner"].includes(user?.profile);
};

const getFirstAllowedPath = user => {
	if (["superadmin", "owner"].includes(user?.profile)) return "/superadmin";

	let userPermissions = user?.menuPermissions;
	let companyPermissions = user?.company?.menuPermissions;

	if (typeof userPermissions === "string" && userPermissions.trim()) {
		try {
			userPermissions = JSON.parse(userPermissions);
		} catch {
			userPermissions = null;
		}
	}

	if (typeof companyPermissions === "string" && companyPermissions.trim()) {
		try {
			companyPermissions = JSON.parse(companyPermissions);
		} catch {
			companyPermissions = null;
		}
	}

	const permissions = Array.isArray(userPermissions)
		? userPermissions
		: Array.isArray(companyPermissions)
			? companyPermissions
			: null;

	if (!Array.isArray(permissions)) return "/";

	return (
		defaultPermissionPaths.find(item => {
			if (!roleCanUsePermission(user, item.permission)) return false;
			if (!permissions.includes(item.permission)) return false;
			if (!Array.isArray(companyPermissions)) return true;
			return companyPermissions.includes(item.permission);
		})?.path ||
		"/"
	);
};

const getResellerLogoutPath = resellerSlug => {
	if (!resellerSlug) return "/login";

	const currentPath = String(window.location.pathname || "");
	if (currentPath.startsWith("/r/")) return `/r/${resellerSlug}`;

	return "/";
};

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});
	const [impersonation, setImpersonation] = useState(() => {
		const stored = localStorage.getItem("impersonation");
		return stored ? JSON.parse(stored) : null;
	});

	api.interceptors.request.use(
		config => {
			const token = localStorage.getItem("token");
			if (token) {
				config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
				setIsAuth(true);
			}
			return config;
		},
		error => {
			Promise.reject(error);
		}
	);

	api.interceptors.response.use(
		response => {
			return response;
		},
		async error => {
			const originalRequest = error.config;
			if (
				error?.response?.status === 403 &&
				!originalRequest._retry &&
				!localStorage.getItem("impersonation")
			) {
				originalRequest._retry = true;

				const { data } = await api.post("/auth/refresh_token");
				if (data) {
					localStorage.setItem("token", JSON.stringify(data.token));
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
				}
				return api(originalRequest);
			}
			if (error?.response?.status === 401) {
				localStorage.removeItem("token");
				api.defaults.headers.Authorization = undefined;
				setIsAuth(false);
			}
			return Promise.reject(error);
		}
	);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					if (impersonation) {
						api.defaults.headers.Authorization = `Bearer ${JSON.parse(token)}`;
						setIsAuth(true);
						setUser(impersonation.user);
						setLoading(false);
						return;
					}

					const { data } = await api.post("/auth/refresh_token");
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					if (data.user?.company?.resellerBranding) {
						applyResellerBranding(data.user.company.resellerBranding);
					}
					setIsAuth(true);
					setUser(data.user);
				} catch (err) {
					toastError(err);
				}
			}
			setLoading(false);
		})();
	}, [impersonation]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	const handleLogin = async userData => {
		setLoading(true);

	try {
		const { data } = await api.post("/auth/login", userData);
		if (data.user?.company?.resellerBranding) {
			applyResellerBranding(data.user.company.resellerBranding);
		}
		localStorage.removeItem("superadminToken");
			localStorage.removeItem("impersonation");
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setImpersonation(null);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push(getFirstAllowedPath(data.user));
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			const resellerBranding = user?.company?.resellerBranding;
			const resellerSlug = resellerBranding?.slug;
			if (resellerBranding) {
				applyResellerBranding(resellerBranding);
			}
			await api.delete("/auth/logout");
			localStorage.removeItem("token");
			localStorage.removeItem("superadminToken");
			localStorage.removeItem("impersonation");
			api.defaults.headers.Authorization = undefined;
			history.replace(getResellerLogoutPath(resellerSlug));
			setIsAuth(false);
			setUser({});
			setImpersonation(null);
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleImpersonate = async targetUserId => {
		const currentToken = localStorage.getItem("token");
		const currentUser = user;

		const { data } = await api.post(`/superadmin/impersonate/${targetUserId}`);
		localStorage.setItem("superadminToken", currentToken);
		localStorage.setItem("token", JSON.stringify(data.token));
		const nextImpersonation = {
			user: data.user,
			originalUser: currentUser,
		};
		localStorage.setItem("impersonation", JSON.stringify(nextImpersonation));
		api.defaults.headers.Authorization = `Bearer ${data.token}`;
		setUser(data.user);
		setImpersonation(nextImpersonation);
		setIsAuth(true);
		history.push(getFirstAllowedPath(data.user));
	};

	const handleStopImpersonation = () => {
		const superadminToken = localStorage.getItem("superadminToken");
		const stored = localStorage.getItem("impersonation");

		if (!superadminToken || !stored) return;

		const parsed = JSON.parse(stored);
		localStorage.setItem("token", superadminToken);
		localStorage.removeItem("superadminToken");
		localStorage.removeItem("impersonation");
		const token = JSON.parse(superadminToken);
		api.defaults.headers.Authorization = `Bearer ${token}`;
		setUser(parsed.originalUser);
		setImpersonation(null);
		setIsAuth(true);
		history.push(getFirstAllowedPath(parsed.originalUser));
	};

	return {
		isAuth,
		user,
		loading,
		impersonation,
		handleLogin,
		handleLogout,
		handleImpersonate,
		handleStopImpersonation,
	};
};

export default useAuth;

